'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'Less Container Load (LCL)', href: '/lcl-transport' },
  { label: 'Airfreight Transport', href: '/airfreight-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Port Operations', href: '/port-operations' },
  { label: 'Cargo Insurance', href: '/cargo-insurance' },
  { label: 'Bank Guarantee', href: '/bank-guarantee' },
];

export default function InlandTransportPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="Inland road transport truck illustration">
                <rect x="180" y="120" width="100" height="80" rx="8" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.06)"/>
                <rect x="192" y="130" width="40" height="28" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(42,143,171,0.1)"/>
                <line x1="240" y1="122" x2="240" y2="200" stroke="#1B6B8A" strokeWidth="2.5"/>
                <rect x="40" y="120" width="148" height="80" rx="4" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.06)"/>
                <line x1="88" y1="120" x2="88" y2="200" stroke="#1B6B8A" strokeWidth="2" strokeDasharray="6 4"/>
                <line x1="136" y1="120" x2="136" y2="200" stroke="#1B6B8A" strokeWidth="2" strokeDasharray="6 4"/>
                <circle cx="80" cy="210" r="18" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <circle cx="80" cy="210" r="8" stroke="#1B6B8A" strokeWidth="3" fill="none"/>
                <circle cx="150" cy="210" r="18" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <circle cx="150" cy="210" r="8" stroke="#1B6B8A" strokeWidth="3" fill="none"/>
                <circle cx="230" cy="210" r="18" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <circle cx="230" cy="210" r="8" stroke="#1B6B8A" strokeWidth="3" fill="none"/>
                <circle cx="264" cy="210" r="18" stroke="#1B6B8A" strokeWidth="4" fill="none"/>
                <circle cx="264" cy="210" r="8" stroke="#1B6B8A" strokeWidth="3" fill="none"/>
                <line x1="20" y1="232" x2="300" y2="232" stroke="#2A8FAB" strokeWidth="3" strokeLinecap="round"/>
                <line x1="20" y1="244" x2="300" y2="244" stroke="#7EC8DC" strokeWidth="2" strokeLinecap="round"/>
                <line x1="80" y1="238" x2="110" y2="238" stroke="#7EC8DC" strokeWidth="2" strokeDasharray="12 8"/>
                <line x1="150" y1="238" x2="180" y2="238" stroke="#7EC8DC" strokeWidth="2" strokeDasharray="12 8"/>
                <circle cx="278" cy="158" r="6" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.15)"/>
                <path d="M20 140 L38 140" stroke="#2A8FAB" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M14 152 L34 152" stroke="#2A8FAB" strokeWidth="2" strokeLinecap="round"/>
                <path d="M18 164 L36 164" stroke="#2A8FAB" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Inland transport
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>World Transport Overseas offers comprehensive <strong style={{ color: '#0F2340' }}>inland transport solutions</strong> covering road and rail freight across Europe and beyond. Our extensive network of own vehicles and trusted carrier partners ensures reliable, cost-effective delivery from port to door or between any two inland points.</p>
                <p>Whether you require a full truck load (FTL) for large consignments or a less-than-truck-load (LTL) groupage service for smaller shipments, our logistics team will design the most efficient routing for your cargo. We provide seamless intermodal solutions combining sea, air, and road transport for complete supply chain coverage.</p>
                <p>Our pan-European coverage extends to all EU member states and Eastern Europe, with door-to-door service including real-time tracking and proactive status updates. WTO's inland transport team coordinates closely with our port operations and customs teams to ensure smooth cargo flow from vessel to final destination.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link href="/transport-quote" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md" style={{ background: '#1A5FAB' }}>
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0F2340' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Our Inland Transport Strengths</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Pan-European Coverage', desc: 'Road and rail freight across all EU member states and beyond into Eastern Europe' },
                { title: 'Multimodal Options', desc: 'Seamless road, rail, and intermodal solutions tailored to your route and cargo type' },
                { title: 'Door-to-Door', desc: 'Complete pickup and delivery service with real-time tracking and status updates' },
                { title: 'Flexible Loads', desc: 'FTL and LTL options — from full truck loads to groupage for smaller consignments' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(126,200,220,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" /></svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service types */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Transport Options</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { name: 'Full Truck Load (FTL)', desc: 'Dedicated truck for your cargo — maximum security, direct routing, and fastest transit times for large shipments.' },
                { name: 'Less Than Truck Load (LTL)', desc: 'Share truck space with compatible cargo — cost-effective solution for smaller road freight consignments.' },
                { name: 'Rail Freight', desc: 'Eco-friendly and cost-efficient rail transport for bulk cargo and container movements across Europe.' },
                { name: 'Intermodal Transport', desc: 'Combined sea-road or air-road solutions using standardised containers for seamless multimodal logistics.' },
              ]?.map((c, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#FFFFFF' }}>{c?.name}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{c?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link key={s?.href} href={s?.href} className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80" style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
